import { useState, type FormEvent } from 'react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../lib/AuthContext';

export function LoginPage() {
  const { user, guest, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!loading && user && !user.mustChangePassword) return <Navigate to="/" replace />;
  if (!loading && user?.mustChangePassword) return <Navigate to="/change-password" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const me = await login(email.trim(), password);
      navigate(me.mustChangePassword ? '/change-password' : '/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box minHeight="100vh" display="flex" flexDirection="column" bgcolor="#fff">
      <Box sx={{ bgcolor: '#F2F4F7', borderBottom: '1px solid #E4E7EC', px: { xs: 2, sm: 4 }, py: 1 }}>
        <Typography fontSize={13} color="#344054">
          An Advanced Substrate Technologies internal site
        </Typography>
      </Box>
      <Box flex={1} display="flex" alignItems="center" justifyContent="center" px={2} py={6} bgcolor="#F9FAFB">
      <Card sx={{ width: '100%', maxWidth: 440 }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Stack spacing={3} mb={3}>
            <Box component="img" src="/ast-logo.png" alt="AST" sx={{ height: 40, alignSelf: 'flex-start' }} />
            <Box>
              <Typography variant="h1" fontSize={32}>
                Sign in to IT Lab
              </Typography>
              <Typography color="text.secondary" mt={0.5}>
                Use your AST work account.
              </Typography>
            </Box>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack component="form" spacing={2} onSubmit={onSubmit}>
            <TextField
              label="Work email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
            />
            <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
              {busy ? <CircularProgress size={22} color="inherit" /> : 'Sign in'}
            </Button>
          </Stack>

          {guest && (
            <Button component={RouterLink} to="/" fullWidth sx={{ mt: 2 }}>
              Continue without signing in
            </Button>
          )}

          <Typography variant="caption" color="text.secondary" display="block" mt={3} textAlign="center">
            Local bootstrap: admin@ast.local · change password on first login
          </Typography>
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
}
